import { useTranslation } from "react-i18next";
import { Download, FileText } from "lucide-react";

import type { MeetingFileEntry } from "../../domain/meetingFile";

/** "2,4 MB" in the reader's language, or nothing when the size is unknown. */
function useFileSize(size: number | undefined): string {
    const { i18n } = useTranslation();
    if (size === undefined) return "";
    const megabytes = size >= 1024 * 1024;
    return new Intl.NumberFormat(i18n.language, {
        style: "unit",
        unit: megabytes ? "megabyte" : "kilobyte",
        unitDisplay: "short",
        maximumFractionDigits: 1,
    }).format(megabytes ? size / (1024 * 1024) : Math.max(0.1, size / 1024));
}

/**
 * A file in the chat: what it is, how far it has got, and — once someone
 * else's has fully arrived — a way to save it.
 *
 * Only ever a download. The link points at a blob typed as plain bytes and
 * carries `download`, so activating it saves the file; nothing the other side
 * sent is shown, previewed or opened inside the call.
 */
export default function MeetingChatFile({ file }: { file: MeetingFileEntry }) {
    const { t } = useTranslation();
    const size = useFileSize(file.size);
    const name = file.name || t("meetingChat.file.fallbackName");

    const status =
        file.status === "failed"
            ? t(file.mine ? "meetingChat.file.sendFailed" : "meetingChat.file.receiveFailed")
            : file.status === "transferring"
              ? t(file.mine ? "meetingChat.file.sending" : "meetingChat.file.receiving", {
                    progress: file.progress,
                })
              : file.mine
                ? t("meetingChat.file.sent")
                : "";

    return (
        <div
            data-testid="meeting-chat-file"
            className={`flex w-[85%] flex-col gap-2 rounded-xl px-3 py-2 text-sm ${
                file.mine
                    ? "bg-room-accent text-white"
                    : "border border-room-line bg-room-base text-room-ink"
            }`}
        >
            <div className="flex min-w-0 items-center gap-2">
                <FileText size={18} aria-hidden="true" className="shrink-0" />
                <div className="flex min-w-0 flex-col">
                    <span className="truncate font-medium" title={name}>
                        {name}
                    </span>
                    {size && <span className="text-xs opacity-80">{size}</span>}
                </div>
            </div>

            {file.status === "transferring" && (
                <div
                    role="progressbar"
                    aria-label={t("meetingChat.file.progressLabel", { name })}
                    aria-valuemin={0}
                    aria-valuemax={100}
                    aria-valuenow={file.progress}
                    className={`h-1 overflow-hidden rounded-full ${
                        file.mine ? "bg-white/30" : "bg-room-line"
                    }`}
                >
                    <div
                        className={`h-full rounded-full transition-[width] duration-[--duration-fast] ${
                            file.mine ? "bg-white" : "bg-room-accent"
                        }`}
                        style={{ width: `${file.progress}%` }}
                    />
                </div>
            )}

            {status && (
                <span
                    className={`text-xs ${
                        file.status === "failed"
                            ? file.mine
                                ? "font-medium"
                                : "font-medium text-room-live"
                            : "opacity-80"
                    }`}
                >
                    {status}
                </span>
            )}

            {!file.mine && file.status === "done" && file.url && (
                <a
                    href={file.url}
                    download={name}
                    aria-label={t("meetingChat.file.download", { name })}
                    className="room-control min-h-11 self-start"
                >
                    <Download size={16} aria-hidden="true" />
                    {t("meetingChat.file.downloadShort")}
                </a>
            )}
        </div>
    );
}
